/* eslint-disable no-nested-ternary */
import React, { Fragment, useRef } from 'react';
import Chart from 'react-apexcharts';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { Spin } from 'antd';
import H3Text from '@Uilib/h3Text';
import { mobileViewBreakPoint } from '@Apis/constants';
import Helpers from '@Apis/helpers';
import './style.scss';

const MetricInvExpirySplit = ({
  data, loading, MONEY_FORMATTER, MONEY, tenantIds,
}) => {
  const ref = useRef(null);
  const colors = [
    '#3f74e3', // blue
    '#74d598', // green
    '#e8bb4a', // yellow
    '#67d0e8', // sky blue
    '#7b76e5', // purple
    '#a4de6c', // neon
    '#f19941', // orange
  ];

  const getExpiryGrouping = (expiryGroups) => {
    const expired = {
      stock_value: 0,
      item_count: 0,
      batch_count: 0,
      category_grouping: [],
    };
    const nearExpiry = {
      stock_value: 0,
      item_count: 0,
      batch_count: 0,
      category_grouping: [],
    };
    const goodStock = {
      stock_value: 0,
      item_count: 0,
      batch_count: 0,
      category_grouping: [],
    };
    const totalStock = {
      stock_value: 0,
      item_count: 0,
      batch_count: 0,
      category_grouping: [],
    };

    for (let i = 0; i < expiryGroups?.length; i++) {
      const group = expiryGroups[i];
      const groupData = {
        stock_value: group?.stock_value || 0,
        item_count: group?.item_count || 0,
        batch_count: group?.batch_count || 0,
        category_grouping: group?.expiry_product_cat_grouping?.map((item) => ({
          id: item?.product_category || 'Other',
          value: item?.stock_value || 0,
          label: item?.product_category || 'Other',
          product_category_id: item?.product_category_id,
        })) || [],
      };

      if (group?.expiry_group === 'Expired') {
        expired.stock_value += groupData.stock_value;
        expired.item_count += groupData.item_count;
        expired.batch_count += groupData.batch_count;
        expired.category_grouping = groupData.category_grouping;
        totalStock.stock_value += Math.log(groupData.stock_value);
        totalStock.item_count += groupData.item_count;
        totalStock.batch_count += groupData.batch_count;
      }

      if (group?.expiry_group === '0%-25% Expiry') {
        nearExpiry.stock_value += groupData.stock_value;
        nearExpiry.item_count += groupData.item_count;
        nearExpiry.batch_count += groupData.batch_count;
        nearExpiry.category_grouping = groupData.category_grouping;
        totalStock.stock_value += Math.log(groupData.stock_value);
        totalStock.item_count += groupData.item_count;
        totalStock.batch_count += groupData.batch_count;
      }

      if (['25%-50% Expiry', '50%-75% Expiry', '75%-100% Expiry'].includes(group?.expiry_group)) {
        goodStock.stock_value += groupData.stock_value;
        goodStock.item_count += groupData.item_count;
        goodStock.batch_count += groupData.batch_count;
        groupData.category_grouping.forEach((item) => {
          const category = item.id;
          const existing = goodStock.category_grouping.find((ctg) => ctg.id === category);
          if (existing) {
            existing.value += item.value;
          } else {
            goodStock.category_grouping.push(item);
          }
        });
      }
    }

    totalStock.stock_value += Math.log(goodStock.stock_value);
    totalStock.item_count += goodStock.item_count;
    totalStock.batch_count += goodStock.batch_count;

    return {
      expired,
      nearExpiry,
      goodStock,
      totalStock,
    };
  };

  const PieEmptyState = () => (
    <div style={{
      width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '10px', flexDirection: 'column', gap: '10px',

    }}
    >
      <div style={{
        width: (ref?.current?.offsetWidth) - 100, height: (ref?.current?.offsetWidth) - 100, backgroundColor: '#ececec', display: 'flex', justifyContent: 'center', alignItems: 'center', borderRadius: '50%',
      }}
      >
        <div style={{
          width: (ref?.current?.offsetWidth) - 150, height: (ref?.current?.offsetWidth) - 150, backgroundColor: 'white', borderRadius: '50%',
        }}
        />
      </div>
    </div>
  );

  const renderChart = (categoryGrouping, title) => {
    const labels = categoryGrouping?.map((item) => item.label);
    const series = categoryGrouping?.map((item) => item.value);

    const onDataPointClick = (event, chartContext, config) => {
      const { seriesIndex, dataPointIndex } = config;
      const value = series[seriesIndex][dataPointIndex];
      const label = labels[dataPointIndex];
      const productCategoryId = categoryGrouping[dataPointIndex]?.product_category_id;
      const expiryStatus = title === 'Expired Stock' ? 'Expired' : title === 'Near Expiry Stock' ? 'Near Expiry' : 'Good Stock';
      window.open(Helpers.appendSystemDefaultFilterIdInURL(`/inventory/manage?tab=/product-batches&page=1&limit=50&tenant=${tenantIds}&sortOrder=ASC&categories=${productCategoryId}&expiry_status=${expiryStatus}&is_pre_fill=not`), '_blank');
    };

    const chartOptions = {
      chart: {
        type: 'donut',
        events: {
          dataPointSelection: onDataPointClick,
        },
      },
      labels,
      colors,
      dataLabels: {
        enabled: false,
      },
      legend: {
        show: false,
      },
      tooltip: {
        enabled: true,
        custom({
          series, seriesIndex,
        }) {
          const value = series[seriesIndex];
          return (
            `${'<div class="custom-tooltip">'
            + '<span class="tooltip-label">'
            + '<span class="dot" style="background-color: '}${colors[seriesIndex]}"></span>${labels[seriesIndex]
            }</span>`
            + `<span class="tooltip-value">${MONEY_FORMATTER.format(value)}</span>`
            + '</div>'
          );
        },
        fixed: {
          enabled: false,
        },
        style: {
          fontSize: '12px',
          fontFamily: undefined,
        },
      },
      responsive: [{
        breakpoint: mobileViewBreakPoint,
        options: {
          chart: {
            width: 300,
          },
          legend: {
            position: 'bottom',
          },
        },
      }],
    };
    return (
      <div className="metric-inv__pie-wrapper">
        <div className="metric-inv__pie-value">
          {MONEY(categoryGrouping?.reduce((sum, item) => sum + item.value, 0))?.substring(0, MONEY(categoryGrouping?.reduce((sum, item) => sum + item.value, 0))?.length - 3)}
        </div>
        {categoryGrouping?.length ? (
          <React.Fragment>
            <Chart options={chartOptions} series={series} type="donut" />
          </React.Fragment>
        ) : <PieEmptyState />}
        <H3Text text={title} className="metric-inv__pie-title" />
      </div>
    );
  };

  const acceptedOverallExpiryGroups = data?.accepted_stock?.[0]?.expiry_grouping;
  const expiryGroups = getExpiryGrouping(acceptedOverallExpiryGroups);

  return (
    <Fragment>
      <div className="metric-inv__expiry_split__wrapper">
        {loading && (
          <div className="widget-loader">
            <Spin />
          </div>
        )}
        <div className="metric-inv__expiry_split__pie-charts">
          <H3Text text="Stock Value by Category" className="metric-inv__expiry_split__title" />
          <div
            className="ant-row"
            style={{
              width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', boxSizing: 'border-box', overflow: 'hidden',
            }}
          >
            <div className="ant-col-md-24 metric-chart-container" ref={ref}>
              {renderChart(expiryGroups?.expired?.category_grouping, 'Expired Stock')}
            </div>
            <div className="ant-col-md-24 metric-chart-container" ref={ref}>
              {renderChart(expiryGroups?.nearExpiry?.category_grouping, 'Near Expiry Stock')}
            </div>
            <div className="ant-col-md-24 metric-chart-container" ref={ref}>
              {renderChart(expiryGroups?.goodStock?.category_grouping, 'Good Stock')}
            </div>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

const mapStateToProps = ({ UserReducers }) => ({
  user: UserReducers.user,
  MONEY: UserReducers.MONEY,
  MONEY_FORMATTER: UserReducers.MONEY_FORMATTER,
});

const mapDispatchToProps = (dispatch) => ({
});

MetricInvExpirySplit.propTypes = {
  user: PropTypes.any,
  MONEY_FORMATTER: PropTypes.func,
  data: PropTypes.any,
};

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(MetricInvExpirySplit));
